import { zodResolver } from '@hookform/resolvers/zod'
import { type CreateUserInput, CreateUserInputSchema, TYPE_ACCOUNT } from '@idc-forms/schemas'
import { Controller, useForm } from 'react-hook-form'
import { useRolesLookupQuery } from '@/features/roles/hooks/use-roles-query'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import { Button } from '@/shared/ui/button'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet
} from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { MultiSelect } from '@/shared/ui/multi-select'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { Switch } from '@/shared/ui/switch'
import { TYPE_ACCOUNT_LABELS } from './users-admin-filter.registry'

type UserFormProps = {
  /** Valores iniciales (edición) — ausente en creación. */
  defaultValues?: Partial<CreateUserInput>
  submitLabel: string
  pending?: boolean
  /** En creación se muestra la nota de contraseña por correo. */
  isCreate?: boolean
  onSubmit: (values: CreateUserInput) => void
  onCancel: () => void
}

/**
 * Formulario de usuario (crear/editar) para el side panel. RHF + zodResolver
 * con el MISMO schema (@idc-forms/schemas) que valida el backend. Sin campos
 * de credenciales: la contraseña inicial la genera el backend y viaja por
 * correo; los cambios de credenciales son self-service.
 */
export function UserForm({
  defaultValues,
  submitLabel,
  pending = false,
  isCreate = false,
  onSubmit,
  onCancel
}: UserFormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<CreateUserInput>({
    resolver: zodResolver(CreateUserInputSchema),
    defaultValues: {
      typeAccount: TYPE_ACCOUNT.USER,
      roles: [],
      active: true,
      ...defaultValues
    }
  })

  const { data: roles = [] } = useRolesLookupQuery()
  const roleOptions = roles.map((role) => ({ value: role._id, label: role.name }))

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 px-3 py-3" noValidate>
      <FieldSet className="gap-4">
        <FieldLegend>Identificación</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="fullname"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.fullname)}>
                <FieldLabel htmlFor="user-fullname">Nombre completo</FieldLabel>
                <Input id="user-fullname" value={field.value ?? ''} onChange={field.onChange} />
                <FieldError errors={[errors.fullname]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="username"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.username)}>
                <FieldLabel htmlFor="user-username">Usuario</FieldLabel>
                <Input
                  id="user-username"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldDescription>Solo letras y números, sin espacios.</FieldDescription>
                <FieldError errors={[errors.username]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.email)}>
                <FieldLabel htmlFor="user-email">Correo</FieldLabel>
                <Input
                  id="user-email"
                  type="email"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                {isCreate && (
                  <FieldDescription>
                    La contraseña inicial se genera automáticamente y se envía a este correo.
                  </FieldDescription>
                )}
                <FieldError errors={[errors.email]} />
              </Field>
            )}
          />

          {/* Avatar oculto hasta habilitar subida de imágenes: hoy solo aceptaría
              una URL cruda, mala UX. El schema lo sigue aceptando (defaultValues
              preserva el existente al editar). */}
        </FieldGroup>
      </FieldSet>

      <FieldSet className="gap-4">
        <FieldLegend>Cuenta y permisos</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="typeAccount"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.typeAccount)}>
                <FieldLabel htmlFor="user-type-account">Tipo de cuenta</FieldLabel>
                <NativeSelect
                  id="user-type-account"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                  }}
                >
                  {Object.values(TYPE_ACCOUNT).map((value) => (
                    <NativeSelectOption key={value} value={value}>
                      {TYPE_ACCOUNT_LABELS[value]}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <FieldError errors={[errors.typeAccount]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="roles"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.roles)}>
                <FieldLabel htmlFor="user-roles">Roles</FieldLabel>
                <MultiSelect
                  id="user-roles"
                  options={roleOptions}
                  value={field.value ?? []}
                  onChange={field.onChange}
                  placeholder="Selecciona roles"
                  searchPlaceholder="Buscar rol..."
                  emptyText="Sin roles"
                />
                <FieldDescription>
                  Los administradores tienen todos los permisos sin importar los roles.
                </FieldDescription>
              </Field>
            )}
          />

          <Controller
            control={control}
            name="active"
            render={({ field }) => (
              <Field orientation="horizontal">
                <FieldLabel htmlFor="user-active">Activo</FieldLabel>
                <Switch
                  id="user-active"
                  checked={field.value ?? true}
                  onCheckedChange={field.onChange}
                />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <SidePanelActions
        actions={[
          {
            label: submitLabel,
            variant: 'default',
            disabled: pending,
            primary: true,
            onClick: () => {
              void handleSubmit(onSubmit)()
            }
          },
          {
            label: 'Cancelar',
            variant: 'outline',
            disabled: pending,
            onClick: onCancel
          }
        ]}
      />
      {/* El submit real va por SidePanelActions; este botón invisible habilita Enter. */}
      <Button type="submit" className="hidden" disabled={pending}>
        {submitLabel}
      </Button>
    </form>
  )
}
